import { useState, useEffect, useCallback } from 'react'
import { FiSmartphone, FiLoader } from 'react-icons/fi'
import { SideModal } from './SideModal'
import ConfirmDialog from './ConfirmDialog'
import { NeuSwitch } from './NeuSwitch'

// A aba LIMITE do app.twms (13/10/2026, pedido do diretor): a consultora vê o
// limite dela e o boleto do limite para pagar; a líder vê a equipe e quem pagou.
// Aqui fica a chave: desligada, só os testadores veem. A configuração mora no
// app (lib/limite.js lá); o fin repassa por routes/limite-boleto.cjs (/app).

interface Config { ativo: boolean; testadores: string[]; por?: string | null; atualizado_em?: string | null }
type Pessoas = Record<string, { nome: string | null; grupo: string | null }>
type Fetch = (url: string, options?: RequestInit) => Promise<Response>

export function LimiteAppChave({ authenticatedFetch, onClose, onMudou }: { authenticatedFetch: Fetch; onClose: () => void; onMudou: (ativo: boolean) => void }) {
  const [cfg, setCfg] = useState<Config | null>(null)
  const [pessoas, setPessoas] = useState<Pessoas>({})
  const [testadores, setTestadores] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmar, setConfirmar] = useState(false)

  const receber = useCallback((d: { success?: boolean; error?: string; config?: Config; pessoas?: Pessoas }) => {
    if (!d.success || !d.config) { setErro(d.error || 'Falha ao falar com o app'); return }
    setCfg(d.config); setPessoas(d.pessoas || {}); setTestadores(d.config.testadores.join(', ')); setErro(null)
    onMudou(d.config.ativo)
  }, [onMudou])
  useEffect(() => {
    authenticatedFetch('/api/limite-boleto/app').then(r => r.json()).then(receber).catch(() => setErro('Erro de conexão'))
  }, [authenticatedFetch, receber])

  const salvar = async (novo: { ativo: boolean; testadores: string[] }) => {
    setConfirmar(false); setBusy(true)
    try {
      const r = await authenticatedFetch('/api/limite-boleto/app', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ config: novo }),
      })
      receber(await r.json())
    } catch { setErro('Erro de conexão') } finally { setBusy(false) }
  }
  const listaTestadores = () => testadores.split(/[\s,;]+/).map(x => x.replace(/\D/g, '')).filter(Boolean)
  const mudouTestadores = !!cfg && listaTestadores().join(',') !== cfg.testadores.join(',')

  return (
    <SideModal icon={FiSmartphone} title="A aba Limite no app" subtitle="app.twms.com.br · consultoras e líderes" onClose={() => { if (!busy) onClose() }}>
      {erro && <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-600 dark:text-red-400">{erro}</div>}
      {/* PLANOS (21/10/2026, subtelas): seções com título 16px, rótulos ≥ 12.5px,
          botões h-9; a nota do Asaas, que ficava solta no fim, virou uma caixa. */}
      {!cfg ? (!erro && <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-2 text-sm text-muted-foreground"><FiLoader className="w-4 h-4 animate-spin" /> lendo o app…</div>) : (<>
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div>
            <h3 className="text-base font-semibold">A aba Limite no app</h3>
            <p className="text-sm text-muted-foreground mt-1">Ligada, cada consultora vê no app o limite de crédito dela, o pedido da semana e o boleto do limite para pagar, com a situação (a pagar, vencido, pago). A líder vê a equipe dela e pode mandar o boleto pelo WhatsApp. Desligada, só os testadores abaixo veem.</p>
          </div>
          <label className="flex items-center gap-3 cursor-pointer w-fit">
            <NeuSwitch on={cfg.ativo} disabled={busy} onClick={() => setConfirmar(true)} />
            <span className="text-sm font-medium">{cfg.ativo ? 'Ligada' : 'Desligada'}</span>
          </label>
          {cfg.atualizado_em && <p className="text-[12.5px] text-muted-foreground">Último ajuste: {new Date(cfg.atualizado_em).toLocaleString('pt-BR')}{cfg.por ? ` por ${cfg.por}` : ''}</p>}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div>
            <h3 className="text-base font-semibold">Testadores</h3>
            <p className="text-sm text-muted-foreground mt-1">Códigos de quem vê a aba mesmo com a chave desligada, separados por vírgula.</p>
          </div>
          <input value={testadores} onChange={e => setTestadores(e.target.value)} placeholder="11003913, 11000000"
            className="neu-input w-full h-9 rounded-lg bg-background border border-border px-3 text-sm tabular-nums" />
          <ul className="text-sm space-y-1">
            {cfg.testadores.map(c => <li key={c}><span className="tabular-nums text-muted-foreground">{c}</span> · {pessoas[c]?.nome || 'código não encontrado'}{pessoas[c]?.grupo ? ` · ${pessoas[c]!.grupo}` : ''}</li>)}
          </ul>
          <button onClick={() => salvar({ ativo: cfg.ativo, testadores: listaTestadores() })} disabled={busy || !mudouTestadores}
            className="h-9 rounded-lg border border-border px-3.5 text-sm hover:bg-secondary disabled:opacity-50">Salvar testadores</button>
        </div>

        <p className="rounded-xl border border-border bg-secondary/40 p-4 text-[13px] leading-relaxed text-muted-foreground">A situação do boleto vem do Asaas e é atualizada a cada 15 minutos. O pagamento do boleto no banco pode levar até 3 dias úteis para aparecer como pago.</p>

        <ConfirmDialog isOpen={confirmar} portal zIndex={100001} closeOnEsc type="warning"
          title={cfg.ativo ? 'Desligar a aba Limite no app?' : 'Ligar a aba Limite no app?'}
          message={cfg.ativo
            ? 'A aba some do app de todas as consultoras e líderes. Só os testadores continuam vendo.'
            : 'Todas as consultoras e líderes com o app passam a ver o limite de crédito e o boleto do limite. A líder vê também a equipe dela.'}
          confirmText={cfg.ativo ? 'Desligar' : 'Ligar para todas'}
          onConfirm={() => salvar({ ativo: !cfg.ativo, testadores: cfg.testadores })} onCancel={() => setConfirmar(false)} />
      </>)}
    </SideModal>
  )
}
